/**
 * api.js  –  Single source of truth for all data fetching.
 *
 * USE_DUMMY_DATA = false → calls the FastAPI backend
 * USE_DUMMY_DATA = true  → returns filtered frontend dummy data (no backend needed)
 */

import apiClient from '../../../utils/axiosConfig';
import { getApiUrl } from '../../../Configurations/config';
import { extractPaginatedPayload } from '../../../utils/serverPagination';
import {
  FIELD_ALIASES,
  normalizeAliasedRows,
  normalizeDivisionOptions,
} from './fieldAliases';
import {
  DUMMY_SUMMARY,
  DUMMY_MONTHLY,
  DUMMY_BDM,
  DUMMY_BRAND,
  DUMMY_STATE,
} from '../data/dummyData';

// ── Toggle ────────────────────────────────────────────────────────────────────
const USE_DUMMY_DATA = false;

// ── Backend client ────────────────────────────────────────────────────────────
const PHARMA_DASHBOARD_BASE = 'supervisor/pharma-dashboard';
const normalizeEndpoint = (path) => String(path || '').replace(/^\/+/, '');
const pharmaPath = (path = '') => `${PHARMA_DASHBOARD_BASE}/${normalizeEndpoint(path)}`;
const unwrapResponse = (response) => {
  const body = response.data;
  if (body && typeof body === 'object' && 'success' in body) {
    return { ...response, data: body.data };
  }
  return response;
};

const api = {
  get: async (path, config) => unwrapResponse(await apiClient.get(getApiUrl(normalizeEndpoint(path)), config)),
};

/**
 * Rows of a response body, with every aliased metric rewritten to its canonical name.
 *
 * Accepts the paginated `{ rows, total, page, page_size }` shape and the legacy flat
 * array alike, and returns `[]` for anything else, so a mapper can call `.map` on the
 * result without a guard. Because `FIELD_ALIASES` is applied here, the mappers below
 * read canonical names only — `r.current_year_sales` rather than
 * `r.current_year_sales ?? r.cy_sales` (Req 7.1). Keys the table says nothing about,
 * such as `expiry_value` or `trend`, pass through untouched (Req 7.3).
 *
 * @param {unknown} payload a response body
 * @returns {Record<string, unknown>[]}
 */
const aliasedRows = (payload) =>
  normalizeAliasedRows(Array.isArray(payload) ? payload : payload?.rows, FIELD_ALIASES);

// ── Dummy helpers ─────────────────────────────────────────────────────────────
const applyFilters = (records, filters = {}) => {
  const { division, state, bdm, brand, month } = filters;
  return records.filter(r => {
    if (division && division !== 'All' && r.division !== division) return false;
    if (state    && state    !== 'All' && r.state    !== state)    return false;
    if (bdm      && bdm      !== 'All' && r.bdm      !== bdm)      return false;
    if (brand    && brand    !== 'All' && r.brand    !== brand)    return false;
    if (month    && month    !== 'All' && r.month    !== month)    return false;
    return true;
  });
};
const fakeDelay = () => new Promise(res => setTimeout(res, 180));

// ── Divisions ─────────────────────────────────────────────────────────────────
// Returns dropdown-ready `[{ value, label }]`. The endpoint answers with plain strings
// (`["ACUTE"]`) or objects (`[{ division: "ACUTE" }]`) depending on deployment, and that
// branch belongs here rather than in `FilterBar` (Req 7.2).
export const getDivisions = async () => {
  const { data } = await api.get(pharmaPath('divisions'));
  return normalizeDivisionOptions(data);
};

// ── Fiscal Years / Months ─────────────────────────────────────────────────────
export const getFiscalYears = async () => {
  const { data } = await api.get(pharmaPath('months'));
  return data;
};

// ── Brand Sales (used by FilterContext for dropdown) ──────────────────────────
export const getBrandSales = async (filters = {}) => {
  const { division, fiscalYear, yearmonth, state, hq, bdm, zm } = filters;
  if (!division || !fiscalYear) return [];

  const { data } = await api.get(pharmaPath('brand'), {
    params: {
      division,
      fiscal_year: fiscalYear,
      ...(yearmonth ? { yearmonth } : {}),
      ...(state ? { state } : {}),
      ...(hq  ? { hqs: hq } : {}),
      ...(bdm ? { bdm }     : {}),
      ...(zm  ? { zm }      : {}),
    },
  });
  return data;
};

// ── BDM Hierarchy (used by FilterContext) ─────────────────────────────────────
export const getBdmHierarchy = async (division = '', fiscalYear = '', yearmonth = '') => {
  if (!division || !fiscalYear) return [];
  const { data } = await api.get(pharmaPath('bdm-hierarchy'), {
    params: {
      division,
      fiscal_year: fiscalYear,
      ...(yearmonth ? { yearmonth } : {}),
    },
  });
  return data;
};

// ── Sales Trends (monthly) ────────────────────────────────────────────────────
export const getMonthPerformance = async (filters = {}) => {
  const { division, fiscalYear, yearmonth, state, bdm, zm, hq, brand } = filters;
  if (!division || !fiscalYear) return [];

  const { data } = await api.get(pharmaPath('sales-trends'), {
    params: {
      division,
      fiscal_year: fiscalYear,
      ...(yearmonth ? { yearmonth } : {}),
      ...(state ? { state } : {}),
      ...(bdm   ? { bdm }   : {}),
      ...(zm    ? { zm }    : {}),
      ...(hq    ? { hq }    : {}),
      ...(brand ? { brand } : {}),
    },
  });

  return aliasedRows(data).map(r => {
    const cy     = +(r.current_year_sales ?? 0);
    const target = +(r.target_value       ?? 0);
    const ly     = +(r.last_year_sales    ?? 0);
    const ttms   = +(r.total_tms          ?? 0);
    const sop    = +(r.sop_count          ?? 0);
    const totalCn = Math.abs(+(r.rate_difference    ?? 0))
                  + Math.abs(+(r.sales_return_value ?? 0))
                  + Math.abs(+(r.expiry_value       ?? 0))
                  + Math.abs(+(r.breakage_value     ?? 0));

    return {
      month:              r.month_name,
      yearmonth:          r.yearmonth,
      cy_sales:           cy,
      target,
      ly_sales:           ly,
      ach_pct:            target > 0 ? +((cy / target) * 100).toFixed(1) : 0,
      grth_pct:           ly > 0 ? +(((cy - ly) / ly) * 100).toFixed(1) : 0,
      rate_difference:    +(r.rate_difference     ?? 0),
      sales_return_value: +(r.sales_return_value  ?? 0),
      expiry_value:       +(r.expiry_value        ?? 0),
      breakage_value:     +(r.breakage_value      ?? 0),
      tms_achieved:       +(r.tms_achieved        ?? 0),
      total_tms:          ttms,
      sop_count:          sop,
      sop_pct:            ttms > 0 ? +(sop / ttms * 100).toFixed(1) : 0,
      pcpm:               +(r.employee_count ?? 0) > 0
                            ? +(cy / +(r.employee_count ?? 1)).toFixed(2) : 0,
      doi:                0,
      total_cn:           totalCn,
      expiry:             Math.abs(+(r.expiry_value       ?? 0)),
      returns:            Math.abs(+(r.sales_return_value ?? 0)),
      rate_diff:          Math.abs(+(r.rate_difference    ?? 0)),
      breakage:           Math.abs(+(r.breakage_value     ?? 0)),
      exp_pct:            cy > 0 ? +((Math.abs(+(r.expiry_value ?? 0)) / cy) * 100).toFixed(1) : 0,
      employee_count:     +(r.employee_count      ?? 0),
    };
  });
};

// ── Sales Summary (KPI cards) ─────────────────────────────────────────────────
// When yearmonth is set → single-month totals from brand-summary
// When no yearmonth    → sum all months from sales-trends (full FY)
export const getSalesSummary = async (filters) => {
  if (USE_DUMMY_DATA) {
    await fakeDelay();
    const rows = applyFilters(DUMMY_MONTHLY, filters);
    if (!rows.length) return null;
    const cy  = rows.reduce((s, r) => s + r.cy_sales, 0);
    const tgt = rows.reduce((s, r) => s + r.target,   0);
    const ly  = rows.reduce((s, r) => s + r.ly_sales, 0);
    return {
      ...DUMMY_SUMMARY,
      cy_sales:        +cy.toFixed(1),
      target:          +tgt.toFixed(1),
      ly_sales:        +ly.toFixed(1),
      achievement_pct: +(cy / tgt * 100).toFixed(1),
      growth_pct:      +(((cy - ly) / ly) * 100).toFixed(1),
      tms_achieved:    50,
      total_tms:       150,
      sop_count:       40,
      sop_pct:         +(40 / 150 * 100).toFixed(1),
      employee_count:  50,
      pcpm:            +(cy / 50).toFixed(2),
      total_cn_pct:    0,
    };
  }
 
  const { division, fiscalYear, yearmonth, state, bdm, zm, hq, brand } = filters ?? {};
  if (!division || !fiscalYear) return null;
 
  let cy = 0, tgt = 0, ly = 0, total_cn = 0;
  let tms_achieved = 0, total_tms = 0, sop_count = 0, employee_count = 0;
 
  if (yearmonth) {
    // ── Single month: pull from brand-summary ─────────────────────────────
    const { data } = await api.get(pharmaPath('brand-summary'), {
      params: {
        division,
        fiscal_year: fiscalYear,
        yearmonth,
        ...(state ? { state } : {}),
        ...(hq    ? { hqs: hq } : {}),
        ...(bdm   ? { bdm }     : {}),
        ...(zm    ? { zm }      : {}),
        ...(brand ? { brand }   : {}),
        page_size: 200,
      },
    });
 
    const rows = aliasedRows(data);
    if (!rows.length) return null;
 
    rows.forEach(r => {
      cy       += +(r.current_year_sales ?? 0);
      tgt      += +(r.target_value       ?? 0);
      ly       += +(r.last_year_sales    ?? 0);
      total_cn += Math.abs(+(r.rate_difference    ?? 0))
                + Math.abs(+(r.sales_return_value ?? 0))
                + Math.abs(+(r.expiry_value       ?? 0))
                + Math.abs(+(r.breakage_value     ?? 0));
    });
 
    // ✅ FIX: SQL now returns consistent division-level TMS/SOP per brand row
    //    Just use the first row's values (they're all the same)
    if (rows.length > 0) {
      tms_achieved   = +(rows[0].tms_achieved   ?? 0);
      total_tms      = +(rows[0].total_tms      ?? 0);
      sop_count      = +(rows[0].sop_count      ?? 0);
      employee_count = +(rows[0].employee_count ?? 0);
    }
 
  } else {
    // ── Full FY: sum monthly trends for sales, fetch brand-summary for TMS/SOP ──
    const trends = await getMonthPerformance(filters);
    if (!trends.length) return null;
 
    trends.forEach(r => {
      cy       += r.cy_sales ?? 0;
      tgt      += r.target   ?? 0;
      ly       += r.ly_sales ?? 0;
      total_cn += Math.abs(r.rate_difference    ?? 0)
                + Math.abs(r.sales_return_value ?? 0)
                + Math.abs(r.expiry_value       ?? 0)
                + Math.abs(r.breakage_value     ?? 0);
    });
 
    // Fetch TMS + SOP + employee_count from brand-summary (full FY, no yearmonth)
    try {
      const { data: bsData } = await api.get(pharmaPath('brand-summary'), {
        params: {
          division,
          fiscal_year: fiscalYear,
          ...(state ? { state } : {}),
          ...(hq    ? { hqs: hq } : {}),
          ...(bdm   ? { bdm }     : {}),
          ...(zm    ? { zm }      : {}),
          ...(brand ? { brand }   : {}),
          page_size: 200,
        },
      });
 
      const bsRows = aliasedRows(bsData);
      if (bsRows.length > 0) {
        // ✅ FIX: SQL now returns consistent division-level TMS/SOP per brand row
        //    Just use the first row's values (they're all the same)
        tms_achieved   = +(bsRows[0].tms_achieved   ?? 0);
        total_tms      = +(bsRows[0].total_tms      ?? 0);
        sop_count      = +(bsRows[0].sop_count      ?? 0);
        employee_count = +(bsRows[0].employee_count ?? 0);
      }
    } catch (err) {
      console.warn('[API] getSalesSummary – brand-summary fetch failed:', err);
    }
  }
 
  const total_cn_pct = cy > 0 ? +((total_cn / cy) * 100).toFixed(2) : 0;
 
  // ✅ Calculate percentages using the aggregated values
  // TMS %: (HQs that met or exceeded budget) / (total HQs)
  const tms_pct = total_tms > 0 ? +(tms_achieved / total_tms * 100).toFixed(1) : 0;
  
  // SOP %: (HQs that strictly exceeded budget) / (total HQs)
  const sop_pct = total_tms > 0 ? +(sop_count / total_tms * 100).toFixed(1) : 0;
 
  return {
    cy_sales:        +cy.toFixed(1),
    target:          +tgt.toFixed(1),
    ly_sales:        +ly.toFixed(1),
    achievement_pct: tgt > 0 ? +(cy / tgt * 100).toFixed(1) : 0,
    growth_pct:      ly  > 0 ? +(((cy - ly) / ly) * 100).toFixed(1) : 0,
    
    // ✅ TMS: count of HQs where actual_sales >= budget (inclusive)
    tms_achieved,
    total_tms,
    tms_pct,
    
    // ✅ SOP: count of HQs where actual_sales > budget (STRICT)
    sop_count,
    sop_pct,
    
    employee_count,
    total_cn_pct,
    pcpm: employee_count > 0 ? +(cy / employee_count).toFixed(2) : 0,
  };
};
 
// ── BDM Performance (table) ───────────────────────────────────────────────────
export const getBDMPerformance = async (filters, page = 1, pageSize = 10) => {
  if (USE_DUMMY_DATA) {
    await fakeDelay();
    const rows = applyFilters(DUMMY_BDM, filters);
    return { rows, total: rows.length, page: 1, page_size: pageSize };
  }

  const { division, fiscalYear, yearmonth, state, bdm, zm, hq, brand } = filters ?? {};
  if (!division || !fiscalYear) return { rows: [], total: 0, page: 1, page_size: pageSize };

  const { data } = await api.get(pharmaPath('bdm-summary'), {
    params: {
      division,
      fiscal_year: fiscalYear,
      ...(yearmonth ? { yearmonth } : {}),
      ...(state ? { state } : {}),
      ...(bdm   ? { bdm_name: bdm } : {}),
      page,
      page_size: pageSize,
    },
  });

  // The alias table replaces the `?? r.cy_sales` / `?? r.target` / `?? r.rate_diff`
  // fallbacks this branch used to carry inline (Req 7.1).
  const rawRows = aliasedRows(data);

  const rows = rawRows.map(r => {
    const cy     = +(r.current_year_sales ?? 0);
    const ly     = +(r.last_year_sales    ?? 0);
    const target = +(r.target_value       ?? 0);
    const tms    = +(r.tms_achieved       ?? 0);
    const ttms   = +(r.total_tms          ?? 0);
    const sop    = +(r.sop_count          ?? 0);

    const total_cn = Math.abs(+(r.rate_difference    ?? 0))
                   + Math.abs(+(r.sales_return_value ?? 0))
                   + Math.abs(+(r.expiry_value       ?? 0))
                   + Math.abs(+(r.breakage_value     ?? 0));

    return {
      bdm:          r.bdm_name,
      ly_sales:     ly,
      target,
      cy_sales:     cy,
      ach_pct:      +(r.achievement_pct ?? (target > 0 ? +((cy / target) * 100).toFixed(1) : 0)),
      grth_pct:     +(r.growth_pct ?? (ly > 0 ? +(((cy - ly) / ly) * 100).toFixed(1) : 0)),
      tms_achieved: tms,
      total_tms:    ttms,
      tms_pct:      ttms > 0 ? +(tms / ttms * 100).toFixed(1) : 0,
      sop_count:    sop,
      sop_pct:      ttms > 0 ? +(sop / ttms * 100).toFixed(1) : 0,
      doi:          +(r.doi ?? 0),
      total_cn,
      expiry:       Math.abs(+(r.expiry_value       ?? 0)),
      returns:      Math.abs(+(r.sales_return_value ?? 0)),
      breakage:     Math.abs(+(r.breakage_value     ?? 0)),
      rate_diff:    Math.abs(+(r.rate_difference    ?? 0)),
    };
  });

  return {
    rows,
    total:     Array.isArray(data) ? rows.length : data?.total ?? rows.length,
    page:      Array.isArray(data) ? page : data?.page ?? page,
    page_size: Array.isArray(data) ? pageSize : data?.page_size ?? pageSize,
  };

  /*
  const cmp = (a, b) => (a || '').trim().toUpperCase() === (b || '').trim().toUpperCase();

  return (data || []).map(bdm => {
    const seen = new Set();
    let cy_sales = 0, ly_sales = 0, target = 0;
    let rate_diff = 0, returns = 0, expiry = 0, breakage = 0;
    // SOP/TMS are stored at HQ level — collect unique HQ values
    let tms_achieved = 0, total_tms = 0, sop_count = 0;
    const seenHqs = new Set();

    (bdm.zms || []).forEach(zm => {
      if (selectedZm && !cmp(zm.zm_name, selectedZm)) return;
      (zm.hqs || []).forEach(hq => {
        if (selectedHq && !cmp(hq.hq_name, selectedHq)) return;

        // Aggregate TMS/SOP once per HQ (not per brand)
        if (!seenHqs.has(hq.hq_name)) {
          seenHqs.add(hq.hq_name);
          tms_achieved += +(hq.tms_achieved ?? 0);
          total_tms    += +(hq.total_tms    ?? 0);
          sop_count    += +(hq.sop_count    ?? 0);
        }

        (hq.brands || []).forEach(br => {
          if (selectedBrand && !cmp(br.brand_name, selectedBrand)) return;
          const key = `${(hq.hq_name || '').toUpperCase()}|${(br.brand_name || '').toUpperCase()}`;
          if (seen.has(key)) return;
          seen.add(key);
          cy_sales  += +(br.current_year_sales ?? 0);
          ly_sales  += +(br.last_year_sales    ?? 0);
          target    += +(br.total_budget       ?? 0);
          rate_diff += +(br.rate_difference    ?? 0);
          returns   += +(br.sales_return_value ?? 0);
          expiry    += +(br.expiry_value       ?? 0);
          breakage  += +(br.breakage_value     ?? 0);
        });
      });
    });

    const total_cn = Math.abs(rate_diff) + Math.abs(returns) + Math.abs(expiry) + Math.abs(breakage);
    const ach_pct  = target   > 0 ? +((cy_sales / target)                * 100).toFixed(1) : 0;
    const grth_pct = ly_sales > 0 ? +(((cy_sales - ly_sales) / ly_sales) * 100).toFixed(1) : 0;
    // TMS %: HQs that met or exceeded budget
    const tms_pct  = total_tms > 0 ? +(tms_achieved / total_tms * 100).toFixed(1) : 0;
    // SOP %: HQs that strictly exceeded budget
    const sop_pct  = total_tms > 0 ? +(sop_count    / total_tms * 100).toFixed(1) : 0;

    return {
      bdm:          bdm.bdm_name,
      ly_sales,
      target,
      cy_sales,
      ach_pct,
      grth_pct,
      tms_achieved,
      total_tms,
      tms_pct,
      sop_count,
      sop_pct,
      doi:          0,
      total_cn,
      expiry:       Math.abs(expiry),
      returns:      Math.abs(returns),
      breakage:     Math.abs(breakage),
      rate_diff:    Math.abs(rate_diff),
    };
  });
  */
};

// ── Brand Performance (table) ─────────────────────────────────────────────────
export const getBrandPerformance = async (filters, page = 1, pageSize = 10) => {
  if (USE_DUMMY_DATA) {
    await fakeDelay();
    const rows = applyFilters(DUMMY_BRAND, filters);
    return { rows, total: rows.length, page: 1, page_size: pageSize };
  }

  const { division, fiscalYear, yearmonth, state, hq, bdm, zm, brand } = filters;
  if (!division || !fiscalYear) return { rows: [], total: 0, page: 1, page_size: pageSize };

  const { data } = await api.get(pharmaPath('brand-summary'), {
    params: {
      division,
      fiscal_year: fiscalYear,
      ...(yearmonth ? { yearmonth } : {}),
      ...(state ? { state } : {}),
      ...(hq    ? { hqs: hq } : {}),
      ...(bdm   ? { bdm }     : {}),
      ...(zm    ? { zm }      : {}),
      ...(brand ? { brand }   : {}),
      page,
      page_size: pageSize,
    },
  });

  const rawRows = aliasedRows(data);

  const rows = rawRows.map(r => {
    const cy     = +(r.current_year_sales ?? 0);
    const target = +(r.target_value       ?? 0);
    const ly     = +(r.last_year_sales    ?? 0);
    const tms    = +(r.tms_achieved       ?? 0);
    const ttms   = +(r.total_tms          ?? 0);
    const sop    = +(r.sop_count          ?? 0);

    return {
      brand_name:    r.brand_name,
      ly_sales:      ly,
      target,
      cy_sales:      cy,
      expiry:        Math.abs(+(r.expiry_value       ?? 0)),
      returns:       Math.abs(+(r.sales_return_value ?? 0)),
      rate_diff:     Math.abs(+(r.rate_difference    ?? 0)),
      breakage:      Math.abs(+(r.breakage_value     ?? 0)),
      total_cn:      Math.abs(+(r.expiry_value       ?? 0))
                   + Math.abs(+(r.sales_return_value ?? 0))
                   + Math.abs(+(r.rate_difference    ?? 0))
                   + Math.abs(+(r.breakage_value     ?? 0)),
      // TMS: HQs where actual >= budget
      tms_achieved:  tms,
      total_tms:     ttms,
      tms_pct:       ttms > 0 ? +(tms / ttms * 100).toFixed(1) : 0,
      // SOP: HQs where actual > budget (strict)
      sop_count:     sop,
      sop_pct:       ttms > 0 ? +(sop / ttms * 100).toFixed(1) : 0,
      employee_count: +(r.employee_count ?? 0),
    };
  });

  return {
    rows,
    total:     Array.isArray(data) ? rows.length : data?.total ?? rows.length,
    page:      Array.isArray(data) ? page : data?.page ?? page,
    page_size: Array.isArray(data) ? pageSize : data?.page_size ?? pageSize,
  };
};

// ── HQ Performance (table, paginated) ────────────────────────────────────────
// Returns { rows, total, page, page_size } from the backend.
export const getHQPerformance = async (filters, page = 1, pageSize = 10) => {
  const { division, fiscalYear, yearmonth, state, bdm, zm, hq, brand } = filters ?? {};
  if (!division || !fiscalYear) return { rows: [], total: 0, page: 1, page_size: pageSize };

  const { data } = await api.get(pharmaPath('hq-summary'), {
    params: {
      division,
      fiscal_year: fiscalYear,
      ...(yearmonth ? { yearmonth } : {}),
      ...(state ? { state } : {}),
      ...(bdm   ? { bdm }   : {}),
      ...(zm    ? { zm }    : {}),
      ...(hq    ? { hq }    : {}),
      ...(brand ? { brand } : {}),
      page,
      page_size: pageSize,
    },
  });

  // Backend returns { rows, total, page, page_size }
  const rawRows = aliasedRows(data);

  const rows = rawRows.map(r => {
    const cy     = +(r.current_year_sales ?? 0);
    const target = +(r.target_value       ?? 0);
    const ly     = +(r.last_year_sales    ?? 0);
    // achievement_percentage = actual % of budget (e.g. 103.5)
    const tms    = +(r.tms_achieved ?? 0);
    const ttms   = +(r.total_tms    ?? 1);
    const sop    = +(r.sop_count    ?? 0);

    return {
      hq_name:    r.hq_name,
      ly_sales:   ly,
      target,
      cy_sales:   cy,
      ach_pct:    +(r.achievement_pct ?? (target > 0 ? +(cy / target * 100).toFixed(1) : 0)),
      grth_pct:   +(r.growth_pct      ?? (ly > 0 ? +(((cy - ly) / ly) * 100).toFixed(1) : 0)),
      tms_achieved: tms,
      total_tms:    ttms,
      tms_pct:      ttms > 0 ? +(tms / ttms * 100).toFixed(1) : 0,
      sop_count:    sop,
      sop_pct:      ttms > 0 ? +(sop / ttms * 100).toFixed(1) : 0,
      pcpm:         0,
      doi:          0,
      total_cn:     Math.abs(+(r.total_cn ?? 0)),
      expiry:       Math.abs(+(r.expiry_value       ?? 0)),
      returns:      Math.abs(+(r.sales_return_value ?? 0)),
      rate_diff:    Math.abs(+(r.rate_difference    ?? 0)),
      breakage:     Math.abs(+(r.breakage_value     ?? 0)),
    };
  });

  return {
    rows,
    total:     Array.isArray(data) ? rows.length : data?.total ?? rows.length,
    page:      Array.isArray(data) ? page : data?.page ?? page,
    page_size: Array.isArray(data) ? pageSize : data?.page_size ?? pageSize,
  };
};

// ── State Performance (table) ─────────────────────────────────────────────────
export const getStatePerformance = async (filters, page = 1, pageSize = 10) => {
  if (USE_DUMMY_DATA) {
    await fakeDelay();
    const rows = applyFilters(DUMMY_STATE, filters);
    return { rows, total: rows.length, page: 1, page_size: pageSize };
  }

  const { division, fiscalYear, yearmonth, state, bdm, zm, hq, brand } = filters;
  if (!division || !fiscalYear) return { rows: [], total: 0, page: 1, page_size: pageSize };

  const { data } = await api.get(pharmaPath('state-summary'), {
    params: {
      division,
      fiscal_year: fiscalYear,
      ...(yearmonth ? { yearmonth } : {}),
      ...(state ? { state } : {}),
      ...(bdm   ? { bdm }   : {}),
      ...(zm    ? { zm }    : {}),
      ...(hq    ? { hq }    : {}),
      ...(brand ? { brand } : {}),
      page,
      page_size: pageSize,
    },
  });

  const rawRows = aliasedRows(data);

  const rows = rawRows.map(r => {
    const cy     = +(r.current_year_sales ?? 0);
    const target = +(r.target_value       ?? 0);
    const ly     = +(r.last_year_sales    ?? 0);
    const tms    = +(r.tms_achieved       ?? 0);
    const ttms   = +(r.total_tms          ?? 0);
    const sop    = +(r.sop_count          ?? 0);

    return {
      state:         r.state,
      ly_sales:      ly,
      target,
      cy_sales:      cy,
      expiry:        Math.abs(+(r.expiry_value       ?? 0)),
      returns:       Math.abs(+(r.sales_return_value ?? 0)),
      rate_diff:     Math.abs(+(r.rate_difference    ?? 0)),
      breakage:      Math.abs(+(r.breakage_value     ?? 0)),
      total_cn:      Math.abs(+(r.expiry_value       ?? 0))
                   + Math.abs(+(r.sales_return_value ?? 0))
                   + Math.abs(+(r.rate_difference    ?? 0))
                   + Math.abs(+(r.breakage_value     ?? 0)),
      // TMS: HQs where actual >= budget
      tms_achieved:  tms,
      total_tms:     ttms,
      tms_pct:       ttms > 0 ? +(tms / ttms * 100).toFixed(1) : 0,
      // SOP: HQs where actual > budget (strict)
      sop_count:     sop,
      sop_pct:       ttms > 0 ? +(sop / ttms * 100).toFixed(1) : 0,
      employee_count: +(r.employee_count ?? 0),
    };
  });

  return {
    rows,
    total:     Array.isArray(data) ? rows.length : data?.total ?? rows.length,
    page:      Array.isArray(data) ? page : data?.page ?? page,
    page_size: Array.isArray(data) ? pageSize : data?.page_size ?? pageSize,
  };
};

// ── Stockist Anomalies ────────────────────────────────────────────────────────
export const getStockistPerformance = async (filters, page = 1, pageSize = 25) => {
  if (USE_DUMMY_DATA) {
    await fakeDelay();
    const names = [
      'Shree Medical', 'Apex Pharma Dist', 'Raj Medical Stores', 'Meditop Suppliers',
      'Sunrise Distributors', 'Global Med Agency', 'Lifeline Pharma', 'National Pharma Co',
      'City Health Mart', 'Prime Stockist', 'Wellness Traders', 'Alpha Med Store',
      'Sigma Distributors', 'Metro Pharma Hub', 'Royal Medical Agency',
    ];
    const rows = names.map(name => {
      const avg    = 200000 + Math.random() * 600000;
      const isHike = Math.random() > 0.5;
      const curr   = isHike ? avg * (1.5 + Math.random() * 0.8) : avg * (0.2 + Math.random() * 0.3);
      const trend  = Array.from({ length: 6 }, (_, i) => ({
        month: `M${i + 1}`,
        sales: +(avg * (0.85 + Math.random() * 0.3)).toFixed(0),
      }));
      return {
        StockistName:      name,
        CurrentMonthSales: +curr.toFixed(0),
        Avg6MonthSales:    +avg.toFixed(0),
        ChangePercentage:  +(((curr - avg) / avg) * 100).toFixed(1),
        PerformanceFlag:   isHike ? 'SUDDEN HIKE' : 'CRITICAL SLUMP',
        TotalCustomers:    names.length,
        Month_1_Sales:     trend[0].sales,
        Month_2_Sales:     trend[1].sales,
        Month_3_Sales:     trend[2].sales,
        Month_4_Sales:     trend[3].sales,
        Month_5_Sales:     trend[4].sales,
        Month_6_Sales:     trend[5].sales,
      };
    });
    return { rows, total: rows.length, page: 1, page_size: pageSize };
  }

  const { division, fiscalYear, state, hq, brand, yearmonth: month, bdm, zm } = filters ?? {};
  if (!division || !fiscalYear) return { rows: [], total: 0, page: 1, page_size: pageSize };

  // Resolve yearmonth — use selected month or default to latest available
  let yearmonth = '';
  if (month) {
    yearmonth = String(month).replace(/-/g, '');
  } else {
    // fiscalYear "2025-26" → end year 2026 → March 2026 = "202603"
    const parts   = fiscalYear.split('-');
    const endYear = parts[0].length === 4
      ? parseInt(parts[0]) + 1
      : parseInt(`20${parts[1]}`);
    yearmonth = `${endYear}03`;
  }

  const { data } = await api.get(pharmaPath('stockist-anomalies'), {
    params: {
      yearmonth,
      division,
      fiscal_year: fiscalYear,
      ...(state ? { state } : {}),
      ...(hq    ? { territory: hq } : {}),
      ...(brand ? { brand }         : {}),
      ...(bdm   ? { bdm }           : {}),
      ...(zm    ? { zm }            : {}),
      page,
      page_size: pageSize,
    },
  });

  // Handles both paginated { rows, total, page, page_size } and legacy flat array
  // responses, echoing the requested page/size when the backend omits them (Req 6.2, 6.3).
  const {
    rows: extractedRows,
    total,
    page: resolvedPage,
    pageSize: resolvedPageSize,
  } = extractPaginatedPayload(data, { page, pageSize });

  // Pagination first, aliases second: `extractPaginatedPayload` owns the envelope, the
  // alias table owns the row keys. `current_month_sales`, `avg_6month_sales` and
  // `change_pct` are already canonical, so this only rewrites the PascalCase spellings
  // (`CurrentMonthSales`, `Avg6MonthSales`, `ChangePercentage`) some deployments send.
  const rawRows = normalizeAliasedRows(extractedRows, FIELD_ALIASES);

  const rows = rawRows.map(r => {
    const trend = Array.isArray(r.trend) ? r.trend : [];
    return {
      StockistName:         r.stockist_name,
      CurrentMonth:         r.current_month,
      CurrentMonthSales:    +(r.current_month_sales    ?? 0),
      MonthlyCustomerCount: +(r.monthly_customer_count ?? 0),
      TotalCustomers:       +(r.total_customers        ?? 0),
      NormalCustomers:      +(r.normal_customers       ?? 0),
      Avg6MonthSales:       +(r.avg_6month_sales       ?? 0),
      ChangePercentage:     +(r.change_pct             ?? 0),
      PerformanceFlag:      r.performance_flag,
      tms_achieved:         +(r.tms_achieved           ?? 0),
      total_tms:            +(r.total_tms              ?? 0),
      sop_count:            +(r.sop_count              ?? 0),
      achievement_percentage: +(r.achievement_percentage ?? 0),
      employee_count:       +(r.employee_count         ?? 0),
      Month_1_Sales:        trend[0]?.sales ?? null,
      Month_2_Sales:        trend[1]?.sales ?? null,
      Month_3_Sales:        trend[2]?.sales ?? null,
      Month_4_Sales:        trend[3]?.sales ?? null,
      Month_5_Sales:        trend[4]?.sales ?? null,
      Month_6_Sales:        trend[5]?.sales ?? null,
    };
  });

  return {
    rows,
    total,
    page:      resolvedPage,
    page_size: resolvedPageSize,
  };
};
